/**
 * Loads an existing track file and turns it into an editable route.
 *
 * Ports `plugin/TracksLoader.js` and `plugin/RouteLoaderConverter.js`. The legacy client
 * used `@mapbox/togeojson` plus `turf.simplify`; both are replaced here by small local
 * implementations so the parsing stays dependency-free and unit testable.
 */

import { LatLng } from '../models/brouter-map.model';

export type TrackFileFormat = 'gpx' | 'kml' | 'geojson';

/** BRouter degrades badly with very long waypoint lists, so imports are simplified. */
export const DEFAULT_MAX_WAYPOINTS = 25;

export const detectFormat = (filename: string): TrackFileFormat | null => {
  const extension = filename.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'gpx':
      return 'gpx';
    case 'kml':
      return 'kml';
    case 'geojson':
    case 'json':
      return 'geojson';
    default:
      return null;
  }
};

/**
 * Parsed with regular expressions rather than `DOMParser`.
 *
 * GPX and KML track geometry is a flat, well-defined structure, and avoiding the DOM keeps
 * this usable outside a browser — including in tests.
 */
const parseGpx = (content: string): LatLng[] => {
  const points: LatLng[] = [];
  // Track points first; fall back to route points for files that only carry a route.
  const pattern = /<(?:trkpt|rtept)\b[^>]*?\blat\s*=\s*"([^"]+)"[^>]*?\blon\s*=\s*"([^"]+)"/gi;
  const reversed = /<(?:trkpt|rtept)\b[^>]*?\blon\s*=\s*"([^"]+)"[^>]*?\blat\s*=\s*"([^"]+)"/gi;

  for (const match of content.matchAll(pattern)) {
    points.push({ lat: Number(match[1]), lng: Number(match[2]) });
  }

  if (points.length === 0) {
    for (const match of content.matchAll(reversed)) {
      points.push({ lat: Number(match[2]), lng: Number(match[1]) });
    }
  }

  return points.filter(isUsable);
};

const parseKml = (content: string): LatLng[] => {
  const points: LatLng[] = [];

  for (const block of content.matchAll(/<coordinates[^>]*>([\s\S]*?)<\/coordinates>/gi)) {
    for (const triple of block[1].trim().split(/\s+/)) {
      const [lng, lat] = triple.split(',').map(Number);
      points.push({ lat, lng });
    }
  }

  return points.filter(isUsable);
};

const parseGeoJson = (content: string): LatLng[] => {
  const parsed: unknown = JSON.parse(content);
  const points: LatLng[] = [];

  const visit = (node: unknown): void => {
    if (!node || typeof node !== 'object') {
      return;
    }
    const candidate = node as Record<string, unknown>;

    if (candidate['type'] === 'LineString' && Array.isArray(candidate['coordinates'])) {
      for (const [lng, lat] of candidate['coordinates'] as number[][]) {
        points.push({ lat, lng });
      }
      return;
    }

    if (candidate['type'] === 'MultiLineString' && Array.isArray(candidate['coordinates'])) {
      for (const line of candidate['coordinates'] as number[][][]) {
        for (const [lng, lat] of line) {
          points.push({ lat, lng });
        }
      }
      return;
    }

    for (const value of Object.values(candidate)) {
      if (Array.isArray(value)) {
        value.forEach(visit);
      } else {
        visit(value);
      }
    }
  };

  visit(parsed);
  return points.filter(isUsable);
};

const isUsable = (point: LatLng): boolean =>
  Number.isFinite(point.lat) &&
  Number.isFinite(point.lng) &&
  Math.abs(point.lat) <= 90 &&
  Math.abs(point.lng) <= 180;

export const parseTrackFile = (content: string, format: TrackFileFormat): LatLng[] => {
  switch (format) {
    case 'gpx':
      return parseGpx(content);
    case 'kml':
      return parseKml(content);
    case 'geojson':
      return parseGeoJson(content);
  }
};

/** Perpendicular distance from `point` to the segment `start`-`end`, in degrees. */
const perpendicularDistance = (point: LatLng, start: LatLng, end: LatLng): number => {
  const dx = end.lng - start.lng;
  const dy = end.lat - start.lat;

  if (dx === 0 && dy === 0) {
    return Math.hypot(point.lng - start.lng, point.lat - start.lat);
  }

  const t = ((point.lng - start.lng) * dx + (point.lat - start.lat) * dy) / (dx * dx + dy * dy);
  const clamped = Math.max(0, Math.min(1, t));

  return Math.hypot(point.lng - (start.lng + clamped * dx), point.lat - (start.lat + clamped * dy));
};

/** Ramer-Douglas-Peucker, matching what `turf.simplify` did for the legacy client. */
export const simplifyTrack = (points: readonly LatLng[], tolerance: number): LatLng[] => {
  if (points.length < 3 || tolerance <= 0) {
    return [...points];
  }

  const first = points[0];
  const last = points[points.length - 1];

  let maxDistance = 0;
  let index = 0;

  for (let i = 1; i < points.length - 1; i++) {
    const distance = perpendicularDistance(points[i], first, last);
    if (distance > maxDistance) {
      maxDistance = distance;
      index = i;
    }
  }

  if (maxDistance <= tolerance) {
    return [first, last];
  }

  const left = simplifyTrack(points.slice(0, index + 1), tolerance);
  const right = simplifyTrack(points.slice(index), tolerance);

  return [...left.slice(0, -1), ...right];
};

export interface SimplifyResult {
  points: LatLng[];
  tolerance: number;
}

/**
 * Finds the gentlest simplification that still fits within `maxPoints`.
 *
 * Replaces the legacy tolerance slider and its `findLowestTolerance` search, which asked
 * the user to tune a value that only mattered because of the same waypoint ceiling.
 */
export const simplifyToMaxPoints = (
  points: readonly LatLng[],
  maxPoints = DEFAULT_MAX_WAYPOINTS
): SimplifyResult => {
  if (points.length <= maxPoints) {
    return { points: [...points], tolerance: 0 };
  }

  let low = 0;
  let high = 1;
  let best = simplifyTrack(points, high);

  // 30 halvings resolve the tolerance far below any meaningful map precision.
  for (let i = 0; i < 30; i++) {
    const middle = (low + high) / 2;
    const candidate = simplifyTrack(points, middle);

    if (candidate.length > maxPoints) {
      low = middle;
    } else {
      best = candidate;
      high = middle;
    }
  }

  return { points: best, tolerance: high };
};
